const doOnceFlags: { [key: string]: boolean; } = {};

/**
 * If the key was passed before, then doesn't execute the func
 * @param {Function} func
 * @param {string} key
 */
export function doOnce(func: () => void, key: string) {
    if (doOnceFlags[key]) { return; }

    func();
    doOnceFlags[key] = true;
}

export function getFunctionName(funcConstructor: any) {
    // for every other browser in the world
    if (funcConstructor.name) {
        return funcConstructor.name;
    }

    // for the pestilence that is ie11
    const matches = /function\s+([^\(]+)/.exec(funcConstructor.toString());
    return matches && matches.length === 2 ? matches[1].trim() : null;
}

export function isFunction(val: any): boolean {
    return !!(val && val.constructor && val.call && val.apply);
}

export function executeInAWhile(funcs: Function[]): void {
    executeAfter(funcs, 400);
}

const executeNextVMTurnFuncs: Function[] = [];
let executeNextVMTurnPending = false;

export function executeNextVMTurn(func: () => void): void {
    executeNextVMTurnFuncs.push(func);

    if (executeNextVMTurnPending) { return; }

    executeNextVMTurnPending = true;
    window.setTimeout(() => {
        const funcsCopy = executeNextVMTurnFuncs.slice();
        executeNextVMTurnFuncs.length = 0;
        executeNextVMTurnPending = false;
        funcsCopy.forEach(func => func());
    }, 0);
}

export function executeAfter(funcs: Function[], milliseconds = 0): void {
    if (funcs.length > 0) {
        window.setTimeout(() => funcs.forEach(func => func()), milliseconds);
    }
}

/**
 * from https://stackoverflow.com/questions/24004791/can-someone-explain-the-debounce-function-in-javascript
 * @param {Function} func The function to be debounced
 * @param {number} wait The time in ms to debounce
 * @param {boolean} immediate If it should run immediately or wait for the initial debounce delay
 * @return {Function} The debounced function
 */
export function debounce(func: (...args: any[]) => void, wait: number, immediate: boolean = false): (...args: any[]) => void {
    // 'private' variable for instance
    // The returned function will be able to reference this due to closure.
    // Each call to the returned function will share this common timer.
    let timeout: any;

    // Calling debounce returns a new anonymous function
    return function(...args: any[]) {
        // reference the context and args for the setTimeout function
        const context = this;

        // Should the function be called now? If immediate is true
        //   and not already in a timeout then the answer is: Yes
        const callNow = immediate && !timeout;

        // This is the basic debounce behaviour where you can call this
        //   function several times, but it will only execute once
        //   [before or after imposing a delay].
        //   Each time the returned function is called, the timer starts over.
        window.clearTimeout(timeout);

        // Set the new timeout
        timeout = window.setTimeout(function() {
            // Inside the timeout function, clear the timeout variable
            // which will let the next execution run when in 'immediate' mode
            timeout = null;

            // Check if the function already ran with the immediate flag
            if (!immediate) {
                // Call the original function with apply
                // apply lets you define the 'this' object as well as the arguments
                //    (both captured before setTimeout)
                func.apply(context, args);
            }
        }, wait);

        // Immediate mode and no wait timer? Execute the function..
        if (callNow) {
            func.apply(context, args);
        }
    };
}

/**
 * @param {Function} func The function to be throttled
 * @param {number} wait The time in ms to throttle
 * @return {Function} The throttled function
 */
export function throttle(func: (...args: any[]) => void, wait: number): (...args: any[]) => void {
    let previousCall = 0;

    return function(...args: any[]) {
        const context = this;
        const currentCall = new Date().getTime();

        if (currentCall - previousCall < wait) { return; }

        previousCall = currentCall;

        func.apply(context, args);
    };
}

export function waitUntil(condition: () => boolean, callback: () => void, timeout: number = 100, timeoutMessage?: string) {
    const timeStamp = new Date().getTime();

    let interval: number | null = null;
    let executed: boolean = false;

    const internalCallback = () => {
        const reachedTimeout = ((new Date().getTime()) - timeStamp) > timeout;
        if (condition() || reachedTimeout) {
            callback();
            executed = true;
            if (interval != null) {
                window.clearInterval(interval);
                interval = null;
            }

            if (reachedTimeout && timeoutMessage) {
                console.warn(timeoutMessage);
            }
        }
    };

    internalCallback();

    if (!executed) {
        interval = window.setInterval(internalCallback, 10);
    }
}

export function compose(...fns: Function[]) {
    return (arg: any) => fns.reduce((composed, f) => f(composed), arg);
}

export function callIfPresent(func: Function): void {
    if (func) { func(); }
}

export const noop = () => { return; };
